import { Container, inject, injectable } from "inversify";

import AddResult from "../connect/result/AddResult";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import AbstractIframePage from "../engine/AbstractIfarmePage";
import IApp from "../engine/IApp";
import IAudio from "../engine/IAudio";
import IEngine from "../engine/IEngine";
import IInfoPages from "../engine/IInfoPages";
import ISymbols from "../engine/pixi/ISymbols";
import IErrorHandler from "../error/IErrorHandler";
import events from "../events/EventsObservable";
import IEventObserver from "../events/IEventObserver";
import { EVENTS } from "../events/baseEvents";
import IGame from "../game/IGame";
import { GAME_EVENTS } from "../game/gameEvents";
import { getter, sec_to_mmss } from "../helpers";
import IStatesManager from "../states/IStatesManager";
import { STATES } from "../states/states";
import System from "../system/System";
import SystemConfig, { CONFIG_PARAMS, MODES } from "../system/SystemConfig";
import { ACTION_EVENTS } from "../ui/UserAction";
import WakeLockHandler from "../ui/WakeLockHandler";
import Wallet, { WALLET_EVENTS } from "../wallet/Wallet";
import { errorMessages } from "../error/errorMessages";
import { errorFactroy } from "../error/MadmaxError";

@injectable()
abstract class CommanderBase implements IEventObserver {
	gamePaused = false;
	gamePauseRequested = false;
	pausedFromState: string = STATES.IDLE;
	wasAutostart: boolean;
  public readonly game: IGame;

  protected system: System;
  protected engine: IEngine;
  protected app: IApp;
  protected symbols: ISymbols;

  public config: SystemConfig;
  protected gameset: any;
  protected errorHandler: IErrorHandler;
  protected audio: IAudio;
  protected states: IStatesManager;
  protected wallet: Wallet;

  public preinfo: any;
  public infoPages: IInfoPages;

  private spinBufferTimeout: any;
  private spinNextRound: boolean;

  result: AddResult = undefined;
  protected history: AbstractIframePage;

  protected constructor(
    @inject(SERVICE_IDENTIFIER.System) system: System,
    @inject(SERVICE_IDENTIFIER.Game) game: IGame,
    @inject(SERVICE_IDENTIFIER.Engine) engine: IEngine
  ) {
    this.system = system;
    this.errorHandler = system.errorHandler;
    this.config = system.config;
    this.gameset = this.config.gameset;
    this.wallet = system.wallet;

    this.game = game;
    this.engine = engine;
    this.app = engine.app;
    this.symbols = engine.symbols;
    this.audio = engine.audio;
    this.states = system.states;
    // this.messages = messages;

    this.preinfo = null;

    events.subscribe(EVENTS.SPIN, this);
    events.subscribe(EVENTS.SKIP, this);
    events.subscribe(EVENTS.FORCESTOP, this);
		events.subscribe(GAME_EVENTS.REQUEST_PAUSE_GAME, (_, pause) => {
			if(this.game.round.isRunning()){
				this.gamePauseRequested = pause
				return
			}
			this.pauseGame(pause)
		})

    events.subscribe(
      [
        ACTION_EVENTS.ACTION_FORCESTOP,
        ACTION_EVENTS.ACTION_SKIP,
        ACTION_EVENTS.ACTION_PREINFO_CLOSE,
        WALLET_EVENTS.WALLET_ON_SET_LINES_COUNT,
      ],
      this
    );
    WakeLockHandler.getInstance();
  }
  pauseGame(pause: boolean, resumeState?: string) {
		const wasPaused = this.gamePaused
		this.gamePaused = pause
		this.gamePauseRequested = false

		if(pause && !wasPaused){
			this.wasAutostart = this.game.isAutoRun()
			this.pausedFromState = this.resolveResumeState(resumeState ?? this.states.getState())
			this.states.setState(STATES.ERROR)
			this.states.lock()
		} else if(!pause && wasPaused){
			this.states.unlock()
			this.states.setState(this.pausedFromState)
		}
		events.dispatch(GAME_EVENTS.GAME_PAUSED, pause)
		if(!pause && wasPaused && this.wasAutostart && this.canResumeAutostart()){
			events.dispatch(EVENTS.SPIN)
		}
	}

	private resolveResumeState(state: string): string {
		return state === STATES.ERROR ? STATES.IDLE : state
	}

	private canResumeAutostart(): boolean {
		return this.pausedFromState === STATES.IDLE || this.pausedFromState === STATES.FS_IDLE
	}

  notify(eventName: string, data?: any, prevData?: any): Promise<any> | void {
    switch (eventName) {
      case EVENTS.SPIN:
				if(this.gamePaused){
					return
				}
        this.spin();
        break;

      case EVENTS.FORCESTOP:
      case ACTION_EVENTS.ACTION_FORCESTOP:
        this.forcestop();
        break;

      case EVENTS.SKIP:
      case ACTION_EVENTS.ACTION_SKIP:
        this.skip();
        break;

      case ACTION_EVENTS.ACTION_PREINFO_CLOSE:
        this.closePreinfo();
        break;
    }
  }

  create(container: Container) {
    this.game.create();
    this.infoPages && this.infoPages.create();
    // @ts-ignore
    this.history = container.get<AbstractIframePage>(
      SERVICE_IDENTIFIER.History
    );
  }

  async runPreinfo() {
    if (!this.preinfo) {
      return;
    }

    // if (this.config.get(CONFIG_PARAMS.MODE) === MODES.REPLAY) {
    //     preinfo_close && preinfo_close();
    //     return;
    // }

    return this.states.setState(STATES.PREINFO);
  }

  closePreinfo() {}

  run() {
    this.soundOnOff();
    this.game.round.isUnfinished() && this.runUnfinished();
  }

  soundOnOff() {
    if (!this.audio.isMuted()) {
      this.audio.audioFadeIn(3000);
    }
  }

  runUnfinished() {
    let delay = getter("game.unfinishedRoundDelay")(this.gameset) || 1000;

    this.states.setState(STATES.SPIN);

    setTimeout(() => {
      this.game.round.setUnfinished(false);
      events.dispatch(EVENTS.SPIN);
    }, delay);
  }

  async spin() {
    try {
			if(this.game.isInFreeSpins()){
				this.result =  await this.game.spinFreespin()
			} else {
        if(this.gamePaused){
          return
        }
        this.wasAutostart = this.game.isAutoRun()
        this.result = await this.game.start();

      }
    } catch (error) {
      this.handleError(error);
      return;
    }
    this.controlResult(this.result);
  }

  skip() {}

  forcestop() {
    if (
      (this.config.get(CONFIG_PARAMS.FORCESTOP_ENABLED) ||
        this.states.getState() === STATES.SEARCHING) &&
      this.config.get(CONFIG_PARAMS.MODE) !== MODES.REPLAY
    ) {
      this.game.forcestop();
    }
  }

  /**
   * @returns {Promise}
   */
  async spinRespin(addResult) {
    return await this.game.respin(addResult);
  }

  controlResult(result) {}

  endRound() {
    if (this.result?.lineWins?.length) {
      this.game.setForcespin(false);
    }

    return this.game
      .endRound()
      .then(() => {
        this.onEndRound();
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  protected handleError(error) {
    this.onError(error);
    this.stopAutostart();
    this.errorHandler.handleError(error);
  }

  async onEndRound() {
    if (!this.game.isInFreeSpins()) this.wallet.recalc();
    if(this.gamePauseRequested){
			this.pauseGame(true, this.game.isInFreeSpins() ? STATES.FS_IDLE : STATES.IDLE)
		}

    // autospin freespins in replay mode
    let autorun =
      (!this.gamePaused && this.game.isAutoRun()) ||
      (this.config.get(CONFIG_PARAMS.MODE) === MODES.REPLAY &&
        this.game.isInFreeSpins());

    await events.dispatch(GAME_EVENTS.GAME_END_ROUND_RECALCED, { autorun });
    if(this.wasAutostart && !this.game.isAutoRun()){
			events.dispatch(GAME_EVENTS.GAME_AUTOSTART_STOP)
		}

    if (autorun || this.spinNextRound) {
      this.game.setForcespin(false);
      events.dispatch(EVENTS.SPIN);
    } else {
      this.states.setState(
        this.game.isInFreeSpins() ? STATES.FS_IDLE : STATES.IDLE
      );
    }
    this.spinNextRound = false;
  }

  stopAutostart() {
    this.game.autostart?.stopAutostart();
    this.states.reset();
  }

  removeBet() {
    this.wallet.removeBet();
  }

  setWin(win = 0) {
    this.wallet.setWin(win);
  }

  addWin(win = 0) {
    this.wallet.addWin(win);
  }

  // showErrorMessage(error) {
  //     this.messages.getErrorMessage(error).openMessage();
  // }

  showErrorMessage(error) {
    const msg = this.errorHandler._showErrorMessage(
      errorFactroy({ code: error })
    );
    msg.setTime?.(sec_to_mmss(256));
    return msg;
  }

  async showErrorMessages() {
    for (const code in errorMessages) {
      console.log(`Showing error message for code: ${code}`);
      await new Promise<void>((resolve) => {
        const msg = this.showErrorMessage(code);
        setTimeout(() => {
          msg.closeMessage();
          resolve();
        }, 200);
      });
    }
  }

  onError(error) {}
}

export default CommanderBase;
